import {Form, FormButton, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {object, string} from 'yup';

import {isNotNil} from 'ramda-adjunct';

import {
  CommonDescriptionAttribute,
  CommonTitleExtensionAttribute,
  VehicleWindshieldAdvertisingSettingsResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {TITLE_EXTENSION_STORAGE_MAX_LENGTH} from '@dms/teas';

import {parseDate, DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {WindshieldSettingsFormValues} from '../types/WindshieldSettingsFormValues';
import {getAttributeByCode} from '../utils/getAttributeByCode';
import {WindshieldTitleExtensionField} from './WindshieldTitleExtensionField';

interface WindshieldSettingsFormProps extends TestIdProps {
  vehicleId: string;
  descriptionLabel: string;
  data: VehicleWindshieldAdvertisingSettingsResponseBody | undefined;
  onSubmit: FormSubmitHandler<WindshieldSettingsFormValues>;
  onHandleEdit: () => void;
  isDisabled: boolean;
}

export function WindshieldSettingsForm(props: WindshieldSettingsFormProps) {
  const formatDateTime = useDateTimeFormatter();

  const values: WindshieldSettingsFormValues = {
    description:
      getAttributeByCode<CommonTitleExtensionAttribute>('description', props.data?.attributes)
        ?.value ?? null,
    title:
      getAttributeByCode<CommonDescriptionAttribute>('title_extension', props.data?.attributes)
        ?.value ?? null,
  };

  return (
    <Form<WindshieldSettingsFormValues>
      values={values}
      resetOptions={{keepDirtyValues: true}}
      schema={schema}
      onSubmit={props.onSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            label={props.descriptionLabel}
            placeholder={i18n.t('page.advertisement.labels.descriptionPlaceholder')}
            name="description"
            type="richText"
            isDisabled={props.isDisabled}
            minRows={2}
            maxRows={15}
            isScrollable
            toolbarOptions={['bold', 'italic', 'underline', 'listOrdered', 'listBullet']}
            data-testid={suffixTestId('description', props)}
          />
          <Grid columns={[1, 2]}>
            <WindshieldTitleExtensionField
              control={control}
              formApi={formApi}
              vehicleId={props.vehicleId}
              isDisabled={props.isDisabled}
              data-testid={props['data-testid']}
            />
          </Grid>
          <Show
            when={
              props.data?.updatedAt && isNotNil(props.data.updatedBy)
                ? {updatedAt: props.data.updatedAt, updatedBy: props.data.updatedBy}
                : null
            }
          >
            {(update) => (
              <Text size="xSmall" color="secondary">
                {`${i18n.t(`page.advertisement.labels.lastUpdateBy`)} ${
                  update.updatedBy
                } ${DOT_CHARACTER} ${formatDateTime('dateTimeShort', parseDate(update.updatedAt))}`}
              </Text>
            )}
          </Show>
          <HStack justify="space-between">
            <Link
              leftIcon="image/edit"
              title={i18n.t('page.advertisement.actions.editParameters')}
              size="small"
              onClick={props.onHandleEdit}
              data-testid="button-advertisement.listingAttributes.edit"
            />
            <Show when={!props.isDisabled}>
              <FormButton
                type="submit"
                control={control}
                isDisabled={props.isDisabled}
                title={i18n.t('general.actions.confirm')}
                data-testid={props['data-testid']}
              />
            </Show>
          </HStack>
        </VStack>
      )}
    </Form>
  );
}

const schema = object().shape({
  title: string()
    .nullable()
    .max(
      TITLE_EXTENSION_STORAGE_MAX_LENGTH,
      i18n.t('general.notifications.titleExtensionMax', {
        characterLimit: TITLE_EXTENSION_STORAGE_MAX_LENGTH,
      })
    ),
  description: string().nullable(),
});
